import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { addNewRelease, updateRelease, selectReleaseById } from "store/releasesSlice";
import { selectCompanyInfo } from "store/companySlice";
import { selectCurrentBoardData } from "store/currentBoardSlice";
import { closeModal } from "store/modalsSlice";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import DatePicker from "layout/atoms/DatePicker";
import Form from "layout/atoms/Form";
import InputText from "layout/atoms/InputText";
import Modal from "layout/atoms/Modal";

const AddReleaseModal = ({ releaseId }) => {
  const dispatch = useDispatch();
  const companyInfo = useSelector(selectCompanyInfo);
  const boardData = useSelector(selectCurrentBoardData);
  const releaseDetails = useSelector((state) => selectReleaseById(state, releaseId));
  const { t } = useTranslation("translation", {
    keyPrefix: "boardsDetails.forms.addRelease",
  });

  const getDefaultValues = () =>
    releaseId
      ? { id: releaseId, name: releaseDetails?.name, releaseDate: releaseDetails?.releaseDate }
      : { companyId: companyInfo.id, name: "", releaseDate: "", boardIds: [boardData.id] };

  const { register, handleSubmit, formState, formState: { errors, isSubmitSuccessful }, reset, setValue } = useForm({
    defaultValues: getDefaultValues(),
  });

  const handleClose = () => dispatch(closeModal("addReleaseModal"));

  const saveRelease = async (data) => {
    handleClose();
    releaseId ? dispatch(updateRelease(data)) : dispatch(addNewRelease(data));
  };

  useEffect(() => {
    reset(getDefaultValues());
  }, []);

  useEffect(() => {
    if (formState.isSubmitSuccessful) reset(getDefaultValues());
  }, [formState, isSubmitSuccessful, reset, releaseDetails]);

  return (
    <Modal open onClose={handleClose}>
      <ContentBox header={<h3>{t(releaseId ? "editMode.header" : "header")}</h3>}>
        <Form onSubmit={handleSubmit(saveRelease)}>
          <InputText
            type="text"
            name="name"
            label={t("fields.name.label")}
            placeholder={t("fields.name.placeholder")}
            register={register}
            errors={errors.name}
            validation={{ required: true, minLength: 1, maxLength: 16 }}
          />
          <DatePicker
            type="text"
            name="releaseDate"
            label={t("fields.releaseDate.label")}
            placeholder={t("fields.releaseDate.placeholder")}
            register={register}
            errors={errors.releaseDate}
            setValue={setValue}
            validation={{ required: true, minLength: 6 }}
          />
          <div>
            <Button type="button" size="lg" color="white" onClick={() => { reset(); handleClose(); }}>
              {t("buttons.cancel")}
            </Button>
            <Button type="submit" size="lg" color="primary">
              {t(releaseId ? "editMode.buttons.save" : "buttons.save")}
            </Button>
          </div>
        </Form>
      </ContentBox>
    </Modal>
  );
};

AddReleaseModal.propTypes = {
  releaseId: PropTypes.string,
};

export default AddReleaseModal;
